import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import ResponsiveImage from '../../components/ResponsiveImage';
import { commonShadow } from '../../utils/uiEffects';

export default function AuthModal({ isOpen, onClose }) {
    const { sendOtp, verifyOtpAndLogin, isAuthenticated } = useAuth();
    const [mode, setMode] = useState('login');
    const [step, setStep] = useState('phone');
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [otpMethod, setOtpMethod] = useState('sms');
    const [error, setError] = useState(null);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [isNewUser, setIsNewUser] = useState(false);
    const [isDeactivated, setIsDeactivated] = useState(false);
    const [loading, setLoading] = useState(false);

    // Close if already authenticated
    useEffect(() => {
        if (isAuthenticated && isOpen) {
            onClose();
        }
    }, [isAuthenticated, isOpen, onClose]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setMode('login');
            setStep('phone');
            setPhone('');
            setOtp(['', '', '', '', '', '']);
            setError(null);
            setName('');
            setEmail('');
            setIsNewUser(false);
            setIsDeactivated(false);
            setLoading(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (phone.length !== 10) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }

        setLoading(true);
        const result = await sendOtp(phone);
        setLoading(false);

        if (result.success) {
            setIsNewUser(result.data?.isNewUser || false);
            setIsDeactivated(result.data?.isDeactivated || false);
            setStep((result.data?.isNewUser || result.data?.isDeactivated) ? 'profile' : 'otp');
        } else {
            setError(result.message || 'Failed to send OTP');
        }
    };

    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto-focus next input
            if (value && index < 5) {
                const nextInput = document.getElementById(`otp-${index + 1}`);
                nextInput?.focus();
            }
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('Text');
        const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');
        if (digits.length > 0) {
            const newOtp = [...otp];
            for (let i = 0; i < digits.length; i++) {
                newOtp[i] = digits[i];
            }
            setOtp(newOtp);

            // Focus the next empty or last input
            const nextIndex = Math.min(digits.length, 5);
            const nextInput = document.getElementById(`otp-${nextIndex}`);
            nextInput?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            prevInput?.focus();
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!otp.every(digit => digit !== '')) {
            setError('Please enter all OTP digits');
            return;
        }

        const otpString = otp.join('');
        setLoading(true);

        // Include name and email if it's a new user or account reactivation
        const result = await verifyOtpAndLogin(
            phone,
            otpString,
            (isNewUser || isDeactivated) ? { fullName: name, email } : undefined
        );

        setLoading(false);

        if (result.success) {
            onClose(); // Close modal on success
        } else {
            setError(result.message || 'Invalid OTP');
        }
    };

    const handleProfileSubmit = (e) => {
        e.preventDefault();
        setError(null);

        if (!name.trim()) {
            setError('Please enter your name');
            return;
        }

        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        // Move to OTP step after collecting profile info
        setStep('otp');
    };

    const handleResendOtp = async () => {
        setError(null);
        setOtp(['', '', '', '', '', '']);

        setLoading(true);
        const result = await sendOtp(phone);
        setLoading(false);

        if (!result.success) {
            setError(result.message || 'Failed to resend OTP');
        }

        // Focus first input
        setTimeout(() => {
            const firstInput = document.getElementById('otp-0');
            firstInput?.focus();
        }, 100);
    };

    const handleChangeNumber = () => {
        setStep('phone');
        setError(null);
        setOtp(['', '', '', '', '', '']);
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                {/* Backdrop click to close */}
                <div className="absolute inset-0" onClick={onClose}></div>

                <motion.div
                    className="relative w-full max-w-md glass-panel-strong rounded-3xl p-10 border-2 border-[#C5A021]/30 z-10 bg-[#0c0806]"
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <i className="ri-close-line text-xl"></i>
                    </button>

                    {/* Logo */}
                    <div className="text-center mb-8">
                        <ResponsiveImage src="/images/optimized/img-763d5bba0c75-640.webp" alt="The Chocolate Room" className="h-16 w-auto mx-auto mb-6" sizes="120px" />
                        <h2 className="font-syncopate text-2xl text-[#F8F4F0] mb-2">
                            {step === 'profile'
                                ? (isDeactivated ? 'Reactivate Account' : 'Complete Your Profile')
                                : mode === 'login' ? 'Welcome Back' : 'Join Us'}
                        </h2>
                        <p className="font-outfit text-[#F8F4F0]/60 text-sm">
                            {step === 'profile'
                                ? (isDeactivated ? 'Update your details to reactivate' : 'Tell us about yourself')
                                : step === 'otp' ? 'Verify your number'
                                : mode === 'login' ? 'Sign in to continue' : 'Create your account'}
                        </p>
                    </div>

                    {/* Error Message */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm font-outfit"
                            >
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence mode="wait">
                        {step === 'phone' && (
                            <motion.form
                                key="phone"
                                onSubmit={handlePhoneSubmit}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="mb-6">
                                    <label className="block font-outfit text-sm text-[#F8F4F0]/80 mb-2">
                                        Mobile Number
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-outfit text-[#F8F4F0]/60">
                                            +91
                                        </span>
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                            placeholder="Enter 10-digit number"
                                            className="w-full !pl-16 pr-4 py-4 bg-[#25160F]/50 border border-[#C5A021]/20 rounded-xl text-[#F8F4F0] font-outfit focus:outline-none focus:border-[#C5A021] transition-colors"
                                            maxLength={10}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                {/* OTP Method Selection */}
                                <div className="mb-6">
                                    <label className="block font-outfit text-sm text-[#F8F4F0]/80 mb-3">
                                        Send OTP via
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setOtpMethod('sms')}
                                            className={`flex-1 py-3 px-4 rounded-xl font-outfit text-sm cursor-pointer whitespace-nowrap transition-all ${otpMethod === 'sms'
                                                ? 'bg-[#C5A021] text-[#140B07] font-semibold'
                                                : 'bg-[#25160F]/50 text-[#F8F4F0]/60 border border-[#C5A021]/20'
                                                }`}
                                        >
                                            <i className="ri-message-3-line mr-2"></i>
                                            SMS
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setOtpMethod('whatsapp')}
                                            className={`flex-1 py-3 px-4 rounded-xl font-outfit text-sm cursor-pointer whitespace-nowrap transition-all ${otpMethod === 'whatsapp'
                                                ? 'bg-[#C5A021] text-[#140B07] font-semibold'
                                                : 'bg-[#25160F]/50 text-[#F8F4F0]/60 border border-[#C5A021]/20'
                                                }`}
                                        >
                                            <i className="ri-whatsapp-line mr-2"></i>
                                            WhatsApp
                                        </button>
                                    </div>
                                </div>

                                <motion.button
                                    type="submit"
                                    className={`w-full py-4 bg-gradient-to-r from-[#C5A021] to-[#D4AF37] text-[#140B07] font-syncopate font-bold text-sm tracking-wider rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${phone.length === 10 ? commonShadow : ''}`}
                                    whileHover={{ scale: phone.length === 10 && !loading ? 1.02 : 1 }}
                                    whileTap={{ scale: phone.length === 10 && !loading ? 0.98 : 1 }}
                                    disabled={phone.length !== 10 || loading}
                                >
                                    {loading ? 'SENDING...' : 'SEND OTP'}
                                </motion.button>
                            </motion.form>
                        )}

                        {step === 'otp' && (
                            <motion.form
                                key="otp"
                                onSubmit={handleOtpSubmit}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <label className="block font-outfit text-sm text-[#F8F4F0]/80">
                                            Enter OTP
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleChangeNumber}
                                            className="font-outfit text-xs text-[#C5A021] cursor-pointer hover:underline whitespace-nowrap"
                                        >
                                            Change Number
                                        </button>
                                    </div>

                                    <p className="font-outfit text-xs text-[#F8F4F0]/60 mb-6">
                                        Sent via {otpMethod === 'sms' ? 'SMS' : 'WhatsApp'} to +91 {phone}
                                    </p>

                                    <div className="flex items-center justify-between gap-2 mb-6">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                id={`otp-${index}`}
                                                type="text"
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                onPaste={handleOtpPaste}
                                                className="w-12 h-14 bg-[#25160F]/50 border border-[#C5A021]/20 rounded-xl text-center text-2xl text-[#F8F4F0] font-syncopate focus:outline-none focus:border-[#C5A021] transition-colors"
                                                maxLength={1}
                                                inputMode="numeric"
                                                autoComplete="off"
                                                autoFocus={index === 0}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <motion.button
                                    type="submit"
                                    className={`w-full py-4 bg-gradient-to-r from-[#C5A021] to-[#D4AF37] text-[#140B07] font-syncopate font-bold text-sm tracking-wider rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${otp.every(d => d) ? commonShadow : ''}`}
                                    whileHover={{ scale: otp.every(d => d) && !loading ? 1.02 : 1 }}
                                    whileTap={{ scale: otp.every(d => d) && !loading ? 0.98 : 1 }}
                                    disabled={otp.some(digit => digit === '') || loading}
                                >
                                    {loading ? 'VERIFYING...' : 'VERIFY & CONTINUE'}
                                </motion.button>

                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={loading}
                                    className="w-full mt-4 py-2 text-[#F8F4F0]/60 font-outfit text-sm cursor-pointer hover:text-[#C5A021] transition-colors whitespace-nowrap"
                                >
                                    Resend OTP
                                </button>
                            </motion.form>
                        )}

                        {step === 'profile' && (
                            <motion.form
                                key="profile"
                                onSubmit={handleProfileSubmit}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="space-y-4 mb-6">
                                    <div>
                                        <label className="block font-outfit text-sm text-[#F8F4F0]/80 mb-2">
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Enter your name"
                                            className="w-full px-4 py-4 bg-[#25160F]/50 border border-[#C5A021]/20 rounded-xl text-[#F8F4F0] font-outfit focus:outline-none focus:border-[#C5A021] transition-colors"
                                            required
                                            autoFocus
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-outfit text-sm text-[#F8F4F0]/80 mb-2">
                                            Email (Optional)
                                        </label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="your.email@example.com"
                                            className="w-full px-4 py-4 bg-[#25160F]/50 border border-[#C5A021]/20 rounded-xl text-[#F8F4F0] font-outfit focus:outline-none focus:border-[#C5A021] transition-colors"
                                        />
                                    </div>

                                    <div className="bg-[#C5A021]/10 border border-[#C5A021]/20 rounded-xl p-4">
                                        <p className="font-outfit text-xs text-[#F8F4F0]/60">
                                            <i className="ri-information-line text-[#C5A021] mr-2"></i>
                                            Your phone number: +91 {phone}
                                        </p>
                                    </div>
                                </div>

                                <motion.button
                                    type="submit"
                                    className={`w-full py-4 bg-gradient-to-r from-[#C5A021] to-[#D4AF37] text-[#140B07] font-syncopate font-bold text-sm tracking-wider rounded-full cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${name.trim() ? commonShadow : ''}`}
                                    whileHover={{ scale: name.trim() ? 1.02 : 1 }}
                                    whileTap={{ scale: name.trim() ? 0.98 : 1 }}
                                    disabled={!name.trim()}
                                >
                                    {isDeactivated ? 'REACTIVATE' : 'CREATE ACCOUNT'}
                                </motion.button>
                            </motion.form>
                        )}
                    </AnimatePresence>

                    {/* Toggle Mode - Only show on phone step */}
                    {step === 'phone' && (
                        <div className="mt-8 text-center">
                            <button
                                onClick={() => {
                                    setMode(mode === 'login' ? 'signup' : 'login');
                                    setError(null);
                                }}
                                className="font-outfit text-sm text-[#F8F4F0]/60 cursor-pointer hover:text-[#C5A021] transition-colors whitespace-nowrap"
                            >
                                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                                <span className="text-[#C5A021] font-semibold">
                                    {mode === 'login' ? 'Sign Up' : 'Sign In'}
                                </span>
                            </button>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
